import { messages, responseCases } from '@constants/errors'
import { Dispatch, SetStateAction } from 'react'
import { FieldValues, UseFormSetError } from 'react-hook-form'

/** Axios and API errors use different shapes; normalize before reading `errors`. */
export const getApiErrorBody = (error: unknown): Record<string, unknown> | null => {
  if (!error || typeof error !== 'object') {
    return null
  }

  const err = error as {
    response?: { data?: Record<string, unknown> }
    data?: Record<string, unknown>
  }

  if (err.response?.data && typeof err.response.data === 'object') {
    return err.response.data
  }

  if (err.data && typeof err.data === 'object') {
    return err.data
  }

  return null
}

const getApiErrorCode = (body: Record<string, unknown> | null): string | undefined => {
  if (!body?.errors || typeof body.errors !== 'object') {
    return undefined
  }

  const errors = body.errors as Record<string, unknown>

  if (typeof errors.error === 'string') {
    return errors.error
  }

  if (typeof errors.message === 'string') {
    return errors.message
  }

  return undefined
}

export const errorSwitcher = (error: unknown, optionalKey: string): string[] | string => {
  const body = getApiErrorBody(error)
  const code = getApiErrorCode(body)

  if (code) {
    switch (code) {
      case responseCases.NUMBER_EXIST:
        return ['number', messages.numberExist]
      case responseCases.EMAIL_EXIST:
      case responseCases.ALREADY_EXIST:
        return ['email', messages.emailExist]
      case responseCases.USER_NOT_FOUND:
      case responseCases.INVALID_CREDENTIALS:
        return ['email', messages.invalidCredential]
      case responseCases.CLUB_PORTAL_ONLY:
        return messages.clubPortalOnly
      default:
        return messages.something
    }
  }

  if (body?.errors && typeof body.errors === 'string') {
    return body.errors as string
  }

  if (error instanceof Error) {
    if (error.message === 'CLUB_PORTAL_ONLY') {
      return messages.clubPortalOnly
    }
    if (error.message) {
      return error.message
    }
  }

  const err = error as { message?: string; code?: string }

  if (err?.code === 'ERR_NETWORK' || err?.message === 'Network Error') {
    return messages.networkError
  }

  if (optionalKey) {
    return [optionalKey, messages.invalidCredential]
  }

  return messages.something
}

interface IGenerateError<T extends FieldValues> {
  error: unknown
  setError: UseFormSetError<T>
  setElseError: Dispatch<SetStateAction<string>>
  optionalKey?: string
}

export const generateError = <T extends FieldValues>({
  error,
  setError,
  setElseError,
  optionalKey = ''
}: IGenerateError<T>) => {
  const result = errorSwitcher(error, optionalKey)

  if (typeof result === 'string') {
    setElseError(result)
  } else if (typeof result === 'object' && result.length > 0) {
    const [key, value] = result

    setError(key as Parameters<UseFormSetError<T>>[0], { message: value })
    setElseError(value)
  }
}
